import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { CloudOff, Loader2, RefreshCw } from "lucide-react";
import { istServerWeg, onServerWeg, serverLebt } from "../api";

/**
 * „Der Server ist gerade nicht da."
 *
 * Bis hierher merkte man das nur daran, dass jede Seite eine eigene rote
 * Zeile zeigte — und weil die Zeilen unterschiedlich hießen, hielt man es
 * für einen Fehler, den man selbst gemacht hat. Ein Neustart dauert zwei bis
 * vier Minuten; in dieser Zeit soll die Oberfläche einen Satz sagen und
 * nicht fünf verschiedene.
 *
 * Sie sagt ihn über allem, weil er für alles gilt, und sie geht von selbst
 * wieder weg: alle paar Sekunden fragt sie nach, und sobald etwas antwortet,
 * verschwindet sie. Ohne Neuladen — was auf dem Bildschirm steht, stimmt ja
 * noch; es kam nur nichts Neues nach.
 */
export function ServerOffline() {
  const weg = useSyncExternalStore(onServerWeg, istServerWeg, () => false);
  const [versucht, setVersucht] = useState(false);
  const [seit, setSeit] = useState<number>(() => Date.now());
  /* Der Zaehler muss laufen. Er stand auf „(0s)" und blieb dort — und eine
     Anzeige, die „versuche es wieder" sagt und sich dabei nicht bewegt,
     sieht genauso eingefroren aus wie das, worueber sie hinwegtroesten
     soll. */
  const [, tick] = useState(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!weg) {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return;
    }
    setSeit(Date.now());
    // Erst nach ein paar Sekunden von selbst nachsehen. Ein einzelner
    // Aussetzer — ein Tunnel, der kurz hustet — soll nicht sofort einen
    // Vorhang aufziehen und gleich wieder wegnehmen.
    timer.current = window.setInterval(() => { void serverLebt(); }, 5000);
    const uhr = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      window.clearInterval(uhr);
    };
  }, [weg]);

  if (!weg) return null;

  const ohneNetz = typeof navigator !== "undefined" && !navigator.onLine;

  return (
    <div className="server-weg" role="alert" aria-live="assertive">
      <div className="server-weg-karte">
        <CloudOff className="icon-svg server-weg-symbol" />
        <h2>{ohneNetz ? "This device is offline" : "The server is not answering"}</h2>
        <p className="muted">
          {ohneNetz
            ? "Check the connection — the page comes back on its own."
            : "It is probably restarting. This page comes back on its own; nothing you did is lost."}
        </p>
        <p className="server-weg-seit">
          <Loader2 className="icon-svg spin" /> trying again… ({sekunden(seit)})
        </p>
        <button
          type="button"
          className="server-weg-knopf"
          disabled={versucht}
          onClick={async () => {
            setVersucht(true);
            await serverLebt();
            setVersucht(false);
          }}
        >
          <RefreshCw className="icon-svg" /> Try now
        </button>
      </div>
    </div>
  );
}

/** Wie lange es schon still ist — in Worten, die man im Vorbeigehen liest. */
function sekunden(seit: number): string {
  const s = Math.max(0, Math.round((Date.now() - seit) / 1000));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)} min`;
}
